import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'remeda'
import { SearchIcon } from '@velasca/icons/search'
import {
  formatCountries,
  type FormattedCountry,
} from '@velasca/utils/format-countries'
import { $settings } from '@velasca/utils/globals'
import { handleError } from '@velasca/utils/handle-error'
import { CountryElement } from '../CountryElement/CountryElement'
import { IconButton } from '../IconButton/IconButton'
import {
  ButtonSearch,
  CloseButton,
  CloseIcon,
  Columns,
  Container,
  InputBar,
  InputContainer,
  Subtitle,
  Title,
} from './SwitchStore.styles'
import type { SwitchStoreProps } from './SwitchStore.types'

export const SwitchStore = ({
  closeSectionHandler,
  countryList = window.countryList,
  showTitle = true,
  showSearchBar = true,
}: SwitchStoreProps): JSX.Element => {
  const countries = formatCountries(countryList, $settings.language)

  const { t } = useTranslation('switch-country')
  const { t: tGlobal } = useTranslation('global')
  const title = t('switchStoreTitle')
  const placeHolder = t('switchStoreSearchBar')
  const [searched, setSearched] = useState('')
  const normalizedCountries = useMemo(() => {
    return countries.filter((country) => {
      if (!country.label) {
        handleError(
          new Error(
            'ERROR: there is a Country with undefined label, please check the data.',
          ),
        )

        return false
      }

      return true
    })
  }, [countries])
  const filteredResult = useMemo(() => {
    return normalizedCountries.filter((country) => {
      if (!country.label) {
        handleError(
          new Error(
            'ERROR: there is a Country with undefined label, please check the data.',
          ),
        )

        return false
      }

      return country.label
        .toLowerCase()
        .normalize('NFD')
        .replaceAll(/[\u0300-\u036f]/g, '')
        .includes(searched.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, ''))
    })
  }, [normalizedCountries, searched])

  const countryListElements = useMemo(() => {
    if (searched) {
      return filteredResult.map((country) => (
        <CountryElement country={country} key={country.label} />
      ))
    }

    // keeping the order coming from $settings.favoritesCountries
    const favorites = $settings.favoritesCountries
      ? $settings.favoritesCountries
          .map((code: string) =>
            normalizedCountries.find((country) => country.code === code),
          )
          // disabling eslint because I want to keep the type predicate
          // eslint-disable-next-line unicorn/prefer-native-coercion-functions
          .filter((c): c is FormattedCountry => Boolean(c))
      : []

    const others = normalizedCountries.filter(
      (country) => !$settings.favoritesCountries?.includes(country.code),
    )

    return (
      <>
        {!isEmpty(favorites) && (
          <>
            <Subtitle>
              {tGlobal('switchStoreFavoritesCountriesLabel')}
              {closeSectionHandler && (
                // TODO: due to the EEA, aria labels and alt has been added in en only. Define how to handle this in the future for a EEA assessment and complete implementation
                <CloseButton aria-label="Close" onClick={closeSectionHandler}>
                  <CloseIcon />
                </CloseButton>
              )}
            </Subtitle>
            {favorites.map((country) => (
              <CountryElement country={country} key={country.label} />
            ))}
            <Subtitle className="all-countries">
              {tGlobal('switchStoreAllCountriesLabel')}
            </Subtitle>
          </>
        )}
        {isEmpty(favorites) && closeSectionHandler && (
          <Subtitle className="all-countries without-favorites">
            {tGlobal('switchStoreAllCountriesLabel')}
            {/* // TODO: due to the EEA,
            aria labels and alt has been added in en only. Define how to handle
            this in the future for a EEA assessment and complete implementation */}
            <CloseButton aria-label="Close" onClick={closeSectionHandler}>
              <CloseIcon />
            </CloseButton>
          </Subtitle>
        )}
        {others.map((country) => (
          <CountryElement country={country} key={country.label} />
        ))}
      </>
    )
  }, [
    closeSectionHandler,
    filteredResult,
    normalizedCountries,
    searched,
    tGlobal,
  ])

  return (
    <Container>
      {showTitle && <Title>{title}</Title>}
      {showSearchBar && (
        <InputContainer>
          <InputBar
            placeholder={placeHolder}
            value={searched}
            onChange={(e) => {
              setSearched(e.target.value.toLowerCase())
            }}
          />
          <ButtonSearch disabled={searched === ''}>
            <IconButton color="#fff">
              <SearchIcon />
            </IconButton>
          </ButtonSearch>
        </InputContainer>
      )}
      <Columns>{countryListElements}</Columns>
    </Container>
  )
}
